/**
 * Trang xem thử ba màn CSKH vừa dựng lại — chỉ chạy ở bản phát triển,
 * `_dev` tự chặn ở bản phát hành.
 *
 * Vì sao đáng có: cả ba màn đều là BẢNG dày cột (khách · hàng hoá · tiền ·
 * trạng thái · thao tác). Bài kiểm khẳng định được "có chữ đó", nhưng không
 * nhìn thay được cột bị bóp, chữ tràn, chip xuống dòng lởm chởm hay số 0 tô
 * đỏ oan. Ba khối dưới đây nạp sẵn dữ liệu vào bộ nhớ đệm truy vấn nên KHÔNG
 * gọi máy chủ và KHÔNG cần đăng nhập.
 *
 * Dữ liệu cố ý dựng theo hình dạng THẬT đo được ngày 01/09/2026: đơn in cũ di
 * trú từ ERP cũ rỗng dòng hàng, bước còn mang mã cũ (`wait_delivery`), nhật ký
 * CSKH phần lớn là `auto_create` do máy ghi và `status_change` có bước trước
 * bằng bước sau.
 */
import { useEffect } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { PageHeader } from "@/components/PageHeader";
import { PrintOrdersListView } from "@/features/cskh/components/PrintOrdersListView";
import { AftercareSubTab } from "@/features/cskh/tabs/AftercareSubTab";
import { ManageTab } from "@/features/cskh/tabs/ManageTab";
import { aftercareKeys, type AftercarePage } from "@/features/cskh/aftercareApi";
import { cskhKeys, type CskhPrintOrder } from "@/features/cskh/api";
import { normalizeStep, sellerKey } from "@/features/cskh/printOrderShared";
import { useAuthV2 } from "@/lib/authv2/store";
import type { components } from "@/lib/api/generated/schema";

export const Route = createFileRoute("/_dev/cskh-preview")({ component: CskhPreview });

// ─────────────────────────── đơn in ───────────────────────────

const po = (
  id: string,
  customer: string,
  step: string,
  extra: Partial<CskhPrintOrder> = {},
): CskhPrintOrder =>
  ({
    id,
    orderId: `order-${id}`,
    step,
    items: { list: [{ productId: "album_20x30", size: "20x30", qty: 1, unitPrice: 1_200_000, vatEnabled: false, vatRateBps: 0 }] },
    shippingCost: 0,
    shippingPayer: "shop",
    address: "",
    notes: "",
    discount: 0,
    discountNote: "",
    billImage: "",
    billNote: "",
    paymentStatus: "unpaid",
    customer,
    phone: "0901 234 567",
    branch: "hn_01",
    createdAt: "2026-08-20T03:00:00Z",
    updatedAt: "2026-08-30T08:15:00Z",
    sellerSub: "sub-cuc",
    sellerName: "Hoàng Cúc",
    createdBySub: "sub-cuc",
    source: "staff",
    shippingZone: "noi_thanh",
    payable: 1_200_000,
    paid: false,
    legacy: false,
    goodsStatus: "approved",
    ...extra,
  }) as unknown as CskhPrintOrder;

const PRINT_ROWS: CskhPrintOrder[] = [
  po("p1", "Chị Nguyễn Thị Thu Hà", "created", { payable: 2_450_000 }),
  po("p2", "Anh Trần Quốc Bảo", "demo", {
    payable: 5_800_000,
    goodsStatus: "pending_approval",
    surchargeId: "sur-1",
    discount: 500_000,
    discountNote: "khách quen giới thiệu 3 đơn",
  }),
  po("p3", "Chị Lê Mai", "printing", { payable: 980_000, paid: true }),
  po("p4", "Cô Phạm Thị Kim Oanh — tên dài để thử tràn cột", "review", {
    payable: 12_600_000,
  }),
  po("p5", "Anh Đỗ Văn Hùng", "delivered", { payable: 3_150_000, paid: true }),
  // Bước còn mang mã ERP cũ: phải hiện "Đang in", KHÔNG được in ra `wait_delivery`.
  po("p6", "Chị Vũ Ngọc Anh", "wait_delivery", { payable: 760_000 }),
  // Đơn di trú: rỗng dòng hàng → phải nói "đơn cũ — không có chi tiết hàng".
  po("p7", "Khách đơn cũ", "created", {
    items: { list: [] } as never,
    payable: 0,
    legacy: true,
    phone: "",
    customer: "",
  }),
];

// ─────────────────────────── hậu mãi ───────────────────────────

type AftercareItem = components["schemas"]["AftercareItem"];

const ac = (
  orderId: string,
  customer: string,
  days: number,
  extra: Partial<AftercareItem> = {},
): AftercareItem =>
  ({
    orderId,
    code: `PF-${orderId.toUpperCase()}`,
    customer,
    phone: "0912 345 678",
    category: "profile",
    product: "pf_basic",
    branch: "hn_01",
    deliveredAt: "2026-08-24T09:00:00Z",
    daysSinceDelivered: days,
    hasEmail: true,
    status: "open",
    touchCount: 0,
    touches: [],
    emailSentAt: null,
    emailSentCount: 0,
    closedReason: "",
    orderValue: 6_500_000,
    ...extra,
  }) as unknown as AftercareItem;

const AFTERCARE_PAGE: AftercarePage = {
  items: [
    ac("a1", "Chị Đặng Thu Trang", 12, { orderValue: 18_900_000 }),
    ac("a2", "Anh Hoàng Minh Đức", 9, { orderValue: 7_200_000, touchCount: 1, touches: [
      { no: 1, channel: "zalo", bySub: "sub-cuc", byName: "Hoàng Cúc", at: "2026-08-29T04:00:00Z" },
    ] as never }),
    ac("a3", "Chị Bùi Thị Lan Phương", 5, { orderValue: 4_100_000, hasEmail: false }),
    ac("a4", "Anh Ngô Thành", 4, { orderValue: 2_800_000, touchCount: 2, touches: [
      { no: 1, channel: "call", bySub: "sub-hong", byName: "Hồng", at: "2026-08-28T04:00:00Z" },
      { no: 2, channel: "zalo", bySub: "sub-hong", byName: "Hồng", at: "2026-08-30T04:00:00Z" },
    ] as never }),
    ac("a5", "Chị Trịnh Khánh Ly", 1, { orderValue: 9_400_000 }),
    ac("a6", "Anh Lý Gia Bảo", 0, { orderValue: 1_500_000 }),
  ],
  total: 6,
  limit: 20,
  offset: 0,
};

// ─────────────────────────── quản lý ───────────────────────────

const SUMMARY: components["schemas"]["DashboardCskhSummary"] = {
  stageCounts: [
    { status: "intake", count: 41 },
    { status: "waiting_selection", count: 18 },
    { status: "retouching", count: 26 },
    { status: "final_sent", count: 9 },
    { status: "fixing", count: 4 },
  ],
  totalActive: 98,
  totalDone: 926,
  overdueList: [
    { orderId: "o1", customer: "Chị Thu Hà", status: "retouching", days: 11, sla: 5, isOverdue: true },
    { orderId: "o2", customer: "Anh Quốc Bảo", status: "final_sent", days: 6, sla: 2, isOverdue: true },
    { orderId: "o3", customer: "Chị Lê Mai", status: "fixing", days: 8, sla: 3, isOverdue: true },
  ],
  actionList: [
    { orderId: "o4", customer: "Cô Kim Oanh", status: "complaint_collect", days: 1, sla: 2, isOverdue: false, isComplaint: true },
    { orderId: "o1", customer: "Chị Thu Hà", status: "retouching", days: 11, sla: 5, isOverdue: true },
  ],
  stageStars: { month: "2026-10", average: 4.6, count: 369 },
  dueSoon: 7,
  todayActions: 34,
};

const ACTIVITY: components["schemas"]["DashboardCskhActivity"] = {
  byStaff: [
    { name: "Hoàng Cúc", actions: 91, customers: 47 },
    { name: "Hồng", actions: 51, customers: 33 },
    { name: "Nguyễn Thị Bích Ngọc", actions: 28, customers: 21 },
  ],
  recent: [
    {
      at: "2026-08-31T10:26:12Z", by: "system", orderId: "o1", orderCode: "PF-1001",
      customer: "Chị Nguyễn Thị Thu Hà", kind: "pipeline", action: "auto_create",
    },
    {
      at: "2026-08-31T09:05:00Z", by: "Hoàng Cúc", orderId: "o2", orderCode: "PF-1002",
      customer: "Anh Trần Quốc Bảo", kind: "pipeline", action: "status_change",
      from: "retouching", to: "final_sent",
    },
    {
      at: "2026-08-31T08:40:00Z", by: "Hoàng Cúc", orderId: "o3", orderCode: "PF-1003",
      customer: "Chị Lê Mai", kind: "pipeline", action: "status_change",
      from: "retouching", to: "retouching", note: "khách xin lùi 2 hôm",
    },
    {
      at: "2026-08-31T08:10:00Z", by: "", orderId: "o4", orderCode: "PF-1004",
      customer: "Cô Phạm Thị Kim Oanh", kind: "complaint", action: "thái độ phục vụ",
      to: "collect",
    },
    {
      at: "2026-08-30T16:20:00Z", by: "Hồng", orderId: "o5", orderCode: "PF-1005",
      customer: "Chị Vũ Ngọc Anh", kind: "pipeline", action: "status_change",
      from: "new_box", to: "wait_final",
    },
  ],
};

// ─────────────────────────── khung ───────────────────────────

/** Mỗi khối một bộ nhớ đệm riêng, nạp sẵn dữ liệu nên không gọi mạng. */
function seeded(fill: (qc: QueryClient) => void) {
  const qc = new QueryClient({
    defaultOptions: { queries: { retry: false, staleTime: Infinity, gcTime: Infinity } },
  });
  fill(qc);
  return qc;
}

/** Trang đầu của bảng Đơn in, đếm giống máy chủ (quy mã bước cũ, đếm người chào). */
const printQc = seeded((qc) => {
  const stepCounts: Record<string, number> = {};
  const sellerCounts: Record<string, number> = {};
  for (const po of PRINT_ROWS) {
    const st = normalizeStep(po.step);
    stepCounts[st] = (stepCounts[st] ?? 0) + 1;
    sellerCounts[sellerKey(po)] = (sellerCounts[sellerKey(po)] ?? 0) + 1;
  }
  qc.setQueryData(
    cskhKeys.printOrdersPage({
      limit: 20,
      offset: 0,
      q: "",
      step: "",
      seller: "",
      unpaid: false,
      sort: "step",
    }),
    {
      items: PRINT_ROWS,
      total: PRINT_ROWS.length,
      stepCounts,
      sellerCounts,
      unpaidCount: PRINT_ROWS.filter((po) => !po.cancelledAt && !po.paid).length,
    },
  );
});

const aftercareQc = seeded((qc) =>
  qc.setQueryData(
    aftercareKeys.list({ filter: "chua_gui", limit: 20, offset: 0 }),
    AFTERCARE_PAGE,
  ),
);

const manageQc = seeded((qc) => {
  qc.setQueryData(["dashboard", "cskh-summary"], SUMMARY);
  qc.setQueryData(["dashboard", "cskh-activity", 30], ACTIVITY);
});

function Block({ title, note, children }: { title: string; note: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-xs text-muted-foreground">{note}</p>
      </div>
      <div className="rounded-lg border bg-surface p-3">{children}</div>
    </section>
  );
}

/**
 * Sổ hậu mãi ẩn CẢ MÀN khi người xem không thuộc phòng CSKH/lễ tân, nên trang
 * xem thử phải tự đóng vai một nhân sự CSKH — không thì nhìn nhầm thành "màn
 * trống".
 *
 * Phải đặt LẠI mỗi khi kho phiên bị dọn: trang này không đăng nhập nên lượt
 * `/auth/refresh` đầu tiên hỏng và `refreshInterceptor` gọi `clear()` — đặt một
 * lần lúc nạp mô-đun là bị xoá ngay sau đó (đã đo: `currentStaff` về null).
 */
const DEV_STAFF = { id: "s1", name: "Hoàng Cúc", role: "staff", department: "cskh" };

function useDevStaff() {
  useEffect(() => {
    const put = () => useAuthV2.setState({ currentStaff: DEV_STAFF });
    put();
    return useAuthV2.subscribe((s) => {
      if (!s.currentStaff) put();
    });
  }, []);
}

function CskhPreview() {
  useDevStaff();
  return (
    <div className="space-y-8 p-6" data-testid="cskh-preview">
      <PageHeader title="Xem thử màn CSKH" description="Dữ liệu dựng sẵn — không gọi máy chủ" />

      <Block
        title="Đơn in"
        note="Dòng cuối là đơn di trú rỗng dòng hàng; dòng 'Chị Vũ Ngọc Anh' mang mã bước cũ wait_delivery."
      >
        <QueryClientProvider client={printQc}>
          <PrintOrdersListView
            canWrite
            onOpen={() => {}}
            onNew={() => {}}
            onNewCustomer={() => {}}
          />
        </QueryClientProvider>
      </Block>

      <Block title="Hậu mãi" note="Quá hạn lên trước; trong cùng nhóm thì đơn giá trị cao đứng trên.">
        <QueryClientProvider client={aftercareQc}>
          <AftercareSubTab />
        </QueryClientProvider>
      </Block>

      <Block title="Quản lý" note="Mọi con số do máy chủ đếm; nhật ký dịch sang tiếng Việt.">
        <QueryClientProvider client={manageQc}>
          <ManageTab />
        </QueryClientProvider>
      </Block>
    </div>
  );
}
